import React, { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import { ReactFlow, ReactFlowProvider, Background, Controls, useReactFlow } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import SydButton from '../../../../commonDesign/Button'
import { dagToGraph } from './util/dagToGraph'
import { autoLayout } from './util/autoLayout'
import StepNode from './nodes/StepNode'
import { withStepDefaults } from './nodes/withStepDefaults'
import WorkflowEdge from './edges/WorkflowEdge'

const nodeTypes = { step: withStepDefaults(StepNode) }
const edgeTypes = { workflow: WorkflowEdge }

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  toolbar: {
    display: 'flex',
    gap: 8,
    padding: '8px 12px',
    borderBottom: '1px solid #E0E0E0',
    backgroundColor: '#FAFAFA',
    alignItems: 'center',
    flexShrink: 0
  },
  canvas: {
    flex: 1,
    minHeight: 0
  }
})

function EditableCanvas ({ steps, dagName, selectedStepId, onSelectStep, onDeselectStep, onConnect, onRemoveStep, onAddStep }) {
  const classes = useStyles()
  const { fitView } = useReactFlow()

  const dag = useMemo(() => ({ name: dagName, steps }), [dagName, steps])

  const { nodes, edges } = useMemo(() => {
    const graph = dagToGraph(dag)
    const editableNodes = graph.nodes.map(n => ({
      ...n,
      data: { ...n.data, editable: true },
      selected: n.id === selectedStepId
    }))
    const layoutNodes = autoLayout(editableNodes, graph.edges)
    return { nodes: layoutNodes, edges: graph.edges }
  }, [dag, selectedStepId])

  const handleNodeClick = useCallback((_event, node) => {
    onSelectStep(node.id)
  }, [onSelectStep])

  const handlePaneClick = useCallback(() => {
    onDeselectStep()
  }, [onDeselectStep])

  const handleConnect = useCallback((connection) => {
    onConnect(connection.target, connection.source)
  }, [onConnect])

  const handleKeyDown = useCallback((event) => {
    if ((event.key === 'Delete' || event.key === 'Backspace') && selectedStepId) {
      onRemoveStep(selectedStepId)
    }
  }, [selectedStepId, onRemoveStep])

  const handleAdd = useCallback(() => {
    const idx = steps.length
    onAddStep({
      dagStepId: `step_${idx}`,
      name: `Step ${idx}`,
      targetAction: 'pass',
      parentStepId: null,
      configuration: {}
    })
    setTimeout(() => fitView({ padding: 0.2, maxZoom: 1, duration: 300 }), 100)
  }, [steps, onAddStep, fitView])

  return (
    <div className={classes.wrapper}>
      <div className={classes.toolbar}>
        <SydButton size='sm' variant='outline' icon='add' onClick={handleAdd}>
          Add Step
        </SydButton>
      </div>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div className={classes.canvas} onKeyDown={handleKeyDown} tabIndex={0}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodeClick={handleNodeClick}
          onPaneClick={handlePaneClick}
          onConnect={handleConnect}
          nodesDraggable={false}
          nodesConnectable
          elementsSelectable
          proOptions={{ hideAttribution: true }}
          fitView
          fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
          minZoom={0.4}
          maxZoom={2}
        >
          <Background />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
    </div>
  )
}

EditableCanvas.propTypes = {
  steps: PropTypes.array.isRequired,
  dagName: PropTypes.string,
  selectedStepId: PropTypes.string,
  onSelectStep: PropTypes.func.isRequired,
  onDeselectStep: PropTypes.func.isRequired,
  onConnect: PropTypes.func.isRequired,
  onRemoveStep: PropTypes.func.isRequired,
  onAddStep: PropTypes.func.isRequired
}

function EditableWorkflowCanvas (props) {
  return (
    <ReactFlowProvider>
      <EditableCanvas {...props} />
    </ReactFlowProvider>
  )
}

EditableWorkflowCanvas.propTypes = EditableCanvas.propTypes

export default EditableWorkflowCanvas
